const DEFAULT_API_BASE = 'http://127.0.0.1:8787';

const resolveApiBase = () => {
  const envUrl = import.meta.env.VITE_CLOUDFLARE_API_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return envUrl.trim().replace(/\/$/, '');
  }
  return DEFAULT_API_BASE;
};

const parseJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const request = async (path, init = {}, accessToken) => {
  const headers = {
    ...(init.headers || {}),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (!headers['Content-Type'] && init.body) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${resolveApiBase()}${path}`, {
    ...init,
    headers,
  });
  const data = await parseJsonSafe(response);
  if (!response.ok) {
    throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
  }
  return data;
};

export class WorkspaceApiClient {
  getAccount(token) {
    return request('/users/me', { method: 'GET' }, token);
  }

  updateAccount(payload, token) {
    return request(
      '/users/me/account',
      { method: 'PATCH', body: JSON.stringify(payload) },
      token
    );
  }

  getSettings(token) {
    return request('/users/me/settings', { method: 'GET' }, token);
  }

  updateSettings(payload, token) {
    return request(
      '/users/me/settings',
      { method: 'PATCH', body: JSON.stringify(payload) },
      token
    );
  }

  getNotifications(token) {
    return request('/users/me/notifications', { method: 'GET' }, token);
  }

  markNotificationRead(notificationId, token) {
    return request(
      `/users/me/notifications/${notificationId}/read`,
      { method: 'PATCH' },
      token
    );
  }

  markAllNotificationsRead(token) {
    return request('/users/me/notifications/read-all', { method: 'PATCH' }, token);
  }

  deleteNotification(notificationId, token) {
    return request(
      `/users/me/notifications/${notificationId}`,
      { method: 'DELETE' },
      token
    );
  }

  createSupportTicket(payload, token) {
    return request(
      '/support/tickets',
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  listAdminSupportTickets(token) {
    return request('/admin/support-tickets', { method: 'GET' }, token);
  }

  adminResetPasswordByTicket(ticketId, token) {
    return request(
      `/admin/support-tickets/${ticketId}/reset-password`,
      { method: 'POST' },
      token
    );
  }

  listClasses(token) {
    return request('/classes', { method: 'GET' }, token);
  }

  createClass(payload, token) {
    return request('/classes', { method: 'POST', body: JSON.stringify(payload) }, token);
  }

  getClassDetail(classId, token) {
    return request(`/classes/${encodeURIComponent(classId)}`, { method: 'GET' }, token);
  }

  updateClass(classId, payload, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      token
    );
  }

  addStudentToClass(classId, studentId, token, overridePayload = null) {
    const payload =
      overridePayload && typeof overridePayload === 'object'
        ? overridePayload
        : { student_id: studentId };
    return request(
      `/classes/${encodeURIComponent(classId)}/students`,
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  removeStudentFromClass(classId, studentId, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}`,
      { method: 'DELETE' },
      token
    );
  }

  getClassStudentDetail(classId, studentId, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}`,
      { method: 'GET' },
      token
    );
  }

  addQuizToClass(classId, payload, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}/quizzes`,
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  removeQuizFromClass(classId, quizId, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}/quizzes/${encodeURIComponent(quizId)}`,
      { method: 'DELETE' },
      token
    );
  }

  getClassQuizResults(classId, quizId, token) {
    return request(
      `/classes/${encodeURIComponent(classId)}/quizzes/${encodeURIComponent(quizId)}/results`,
      { method: 'GET' },
      token
    );
  }

  listChildren(token) {
    return request('/children', { method: 'GET' }, token);
  }

  getChildDetail(studentId, token) {
    return request(`/children/${encodeURIComponent(studentId)}`, { method: 'GET' }, token);
  }

  getMyStudentProfile(token) {
    return request('/students/me/profile', { method: 'GET' }, token);
  }

  updateMyStudentProfile(payload, token) {
    return request('/students/me/profile', { method: 'PATCH', body: JSON.stringify(payload) }, token);
  }

  listMyParents(token) {
    return request('/students/me/parents', { method: 'GET' }, token);
  }

  addMyParent(parentIdOrEmail, relation, token, overridePayload = null) {
    const payload =
      overridePayload && typeof overridePayload === 'object'
        ? overridePayload
        : String(parentIdOrEmail || '').includes('@')
          ? { parent_email: parentIdOrEmail, relation }
          : { parent_id: parentIdOrEmail, relation };
    return request(
      '/students/me/parents',
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  removeMyParent(parentId, token) {
    return request(`/students/me/parents/${encodeURIComponent(parentId)}`, { method: 'DELETE' }, token);
  }
}

